const defineElement = (name, klass) => {
  if (!customElements.get(name)) customElements.define(name, klass);
};

defineElement(
  'ow-read-more',
  class extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-ow-read-more-toggle]');
      this.content = this.querySelector('[data-ow-read-more-content]');
      if (!this.button || !this.content) return;
      this.button.addEventListener('click', () => this.toggle());
    }

    toggle() {
      const expanded = this.button.getAttribute('aria-expanded') === 'true';
      this.button.setAttribute('aria-expanded', String(!expanded));
      this.content.hidden = expanded;
      this.button.querySelector('[data-more]').hidden = !expanded;
      this.button.querySelector('[data-less]').hidden = expanded;
    }
  }
);

defineElement(
  'ow-faqs',
  class extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(index));
        tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
      });
    }

    select(index, focus = false) {
      this.tabs.forEach((tab, tabIndex) => {
        const active = tabIndex === index;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        this.panels[tabIndex].hidden = !active;
      });
      if (focus) this.tabs[index].focus();
    }

    onKeydown(event, index) {
      let next = index;
      if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
      else if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = this.tabs.length - 1;
      else return;
      event.preventDefault();
      this.select(next, true);
    }
  }
);

defineElement(
  'ow-store-status',
  class extends HTMLElement {
    connectedCallback() {
      this.status = this.querySelector('[data-ow-status]');
      this.schedule = JSON.parse(this.querySelector('script[type="application/json"]')?.textContent || '{}');
      if (!this.status || !this.schedule.timeZone) return;
      this.update();
      this.timer = window.setInterval(() => this.update(), 60000);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }

    update() {
      try {
        const parts = new Intl.DateTimeFormat('en-US', {
          timeZone: this.schedule.timeZone,
          weekday: 'short',
          hour: '2-digit',
          minute: '2-digit',
          hourCycle: 'h23'
        }).formatToParts(new Date());
        const value = (type) => parts.find((part) => part.type === type)?.value;
        const day = value('weekday')?.toLowerCase();
        const minutes = Number(value('hour')) * 60 + Number(value('minute'));
        const hours = this.schedule.days?.[day];
        const toMinutes = (time) => {
          const [hour, minute] = String(time || '').split(':').map(Number);
          return Number.isFinite(hour) && Number.isFinite(minute) ? hour * 60 + minute : null;
        };
        const opensAt = toMinutes(hours?.open);
        const closesAt = toMinutes(hours?.close);
        const open = Boolean(hours && opensAt !== null && closesAt !== null && minutes >= opensAt && minutes < closesAt);
        this.status.dataset.state = open ? 'open' : 'closed';
        this.status.textContent = open ? this.schedule.openLabel : this.schedule.closedLabel;
      } catch (error) {
        this.status.dataset.state = 'unknown';
      }
    }
  }
);

defineElement(
  'ow-lazy-video',
  class extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button[data-video-id]');
      this.button?.addEventListener('click', () => this.play());
    }

    play() {
      const id = this.button.dataset.videoId.split('&')[0];
      const iframe = document.createElement('iframe');
      iframe.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?autoplay=1`;
      iframe.title = this.button.dataset.title || 'Product video';
      iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
      iframe.allowFullscreen = true;
      iframe.loading = 'lazy';
      this.replaceChildren(iframe);
    }
  }
);

defineElement(
  'ow-responsive-details',
  class extends HTMLElement {
    connectedCallback() {
      this.media = window.matchMedia('(min-width: 990px)');
      this.sync = () => {
        this.querySelectorAll('details[data-desktop-open]').forEach((details) => {
          details.open = this.media.matches;
        });
      };
      this.sync();
      this.media.addEventListener('change', this.sync);
    }

    disconnectedCallback() {
      this.media?.removeEventListener('change', this.sync);
    }
  }
);

class OwPdpGallery {
  constructor(gallery) {
    this.gallery = gallery;
    this.desktop = window.matchMedia('(min-width: 990px)');
    this.progress = document.createElement('div');
    this.progress.className = 'ow-pdp-gallery__progress';
    this.progress.setAttribute('aria-hidden', 'true');
    this.thumb = document.createElement('span');
    this.thumb.className = 'ow-pdp-gallery__progress-thumb';
    this.progress.append(this.thumb);
    this.gallery.append(this.progress);
    this.gallery.classList.add('ow-pdp-gallery');

    this.onScroll = () => this.queueUpdate();
    this.onKeydown = (event) => this.handleKeydown(event);
    this.onMediaChange = () => this.bindScroller();
    this.desktop.addEventListener('change', this.onMediaChange);
    this.resizeObserver = new ResizeObserver(() => this.queueUpdate());
    this.mutationObserver = new MutationObserver(() => this.queueBindScroller());
    this.mutationObserver.observe(this.gallery, { childList: true, subtree: true });
    this.bindScroller();
  }

  queueBindScroller() {
    window.cancelAnimationFrame(this.bindFrame);
    this.bindFrame = window.requestAnimationFrame(() => this.bindScroller());
  }

  bindScroller() {
    const nextScroller = this.desktop.matches
      ? this.gallery.querySelector('.media-gallery__grid')
      : this.gallery.querySelector('slideshow-slides');

    if (nextScroller === this.scroller) {
      this.queueUpdate();
      return;
    }

    this.scroller?.removeEventListener('scroll', this.onScroll);
    this.scroller?.removeEventListener('keydown', this.onKeydown);
    if (this.scroller) this.resizeObserver.unobserve(this.scroller);
    this.scroller = nextScroller;

    if (!this.scroller) {
      this.progress.hidden = true;
      return;
    }

    if (!this.scroller.hasAttribute('tabindex')) this.scroller.tabIndex = 0;
    if (!this.scroller.hasAttribute('aria-label')) this.scroller.setAttribute('aria-label', 'Product media gallery');
    this.scroller.addEventListener('scroll', this.onScroll, { passive: true });
    this.scroller.addEventListener('keydown', this.onKeydown);
    this.resizeObserver.observe(this.scroller);
    this.resizeObserver.observe(this.progress);
    this.queueUpdate();
  }

  handleKeydown(event) {
    if (!this.scroller) return;

    const desktop = this.desktop.matches;
    const forward = desktop ? ['ArrowDown', 'PageDown'] : ['ArrowRight', 'PageDown'];
    const backward = desktop ? ['ArrowUp', 'PageUp'] : ['ArrowLeft', 'PageUp'];
    const clientSize = desktop ? this.scroller.clientHeight : this.scroller.clientWidth;
    const scrollSize = desktop ? this.scroller.scrollHeight : this.scroller.scrollWidth;
    const current = desktop ? this.scroller.scrollTop : this.scroller.scrollLeft;
    let target;

    if (forward.includes(event.key)) target = current + clientSize;
    else if (backward.includes(event.key)) target = current - clientSize;
    else if (event.key === 'Home') target = 0;
    else if (event.key === 'End') target = scrollSize;
    else return;

    event.preventDefault();
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    this.scroller.scrollTo(desktop ? { top: target, behavior } : { left: target, behavior });
  }

  queueUpdate() {
    window.cancelAnimationFrame(this.updateFrame);
    this.updateFrame = window.requestAnimationFrame(() => this.update());
  }

  update() {
    if (!this.scroller?.isConnected) {
      this.bindScroller();
      return;
    }

    const desktop = this.desktop.matches;
    const items = [...this.scroller.children].filter((item) => !item.hidden);
    this.progress.hidden = false;
    const trackSize = desktop ? this.progress.clientHeight : this.progress.clientWidth;
    const clientSize = desktop ? this.scroller.clientHeight : this.scroller.clientWidth;
    const scrollSize = desktop ? this.scroller.scrollHeight : this.scroller.scrollWidth;
    const scrollPosition = desktop ? this.scroller.scrollTop : this.scroller.scrollLeft;
    const scrollRange = Math.max(0, scrollSize - clientSize);

    this.progress.dataset.axis = desktop ? 'vertical' : 'horizontal';
    this.progress.hidden = items.length < 2 || scrollRange < 1 || trackSize < 1;
    if (this.progress.hidden) return;

    const thumbSize = Math.max(desktop ? 70 : 70, trackSize / items.length);
    const travel = Math.max(0, trackSize - thumbSize);
    const offset = scrollRange > 0 ? (scrollPosition / scrollRange) * travel : 0;
    this.thumb.style.setProperty('--ow-gallery-progress-size', `${Math.min(trackSize, thumbSize)}px`);
    this.thumb.style.setProperty('--ow-gallery-progress-offset', `${Math.max(0, Math.min(travel, offset))}px`);
  }
}

const enhancePdpGalleries = (root = document) => {
  root.querySelectorAll?.('.product-information__media media-gallery:not([data-ow-gallery-enhanced])').forEach((gallery) => {
    gallery.dataset.owGalleryEnhanced = 'true';
    gallery.owPdpGallery = new OwPdpGallery(gallery);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => enhancePdpGalleries(), { once: true });
} else {
  enhancePdpGalleries();
}

new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (node instanceof Element) enhancePdpGalleries(node.matches('media-gallery') ? node.parentElement : node);
    }
  }
}).observe(document.documentElement, { childList: true, subtree: true });

defineElement(
  'ow-product-support',
  class extends HTMLElement {
    connectedCallback() {
      this.bindAffirmMessage();
      if (!this.affirmMessage) return;

      this.section = this.closest('.shopify-section');
      this.onProductSelect = (event) => {
        if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
        event.promise
          ?.then(({ variant, detail }) => {
            const cents = detail?.resource?.price ??
              (variant?.price?.amount ? Math.round(Number(variant.price.amount) * 100) : null);
            if (!Number.isFinite(Number(cents))) return;
            this.bindAffirmMessage();
            if (!this.affirmMessage) return;
            this.affirmMessage.dataset.amount = String(cents);
            this.refreshAffirm();
          })
          .catch((error) => {
            if (error?.name !== 'AbortError') console.warn('[ow-product-support] Affirm refresh failed', error);
          });
      };

      this.section?.addEventListener('shopify:product:select', this.onProductSelect);
      this.refreshAffirm();
    }

    disconnectedCallback() {
      this.section?.removeEventListener('shopify:product:select', this.onProductSelect);
      this.affirmObserver?.disconnect();
    }

    bindAffirmMessage() {
      const message = this.querySelector('[data-ow-affirm-message]');
      if (message === this.affirmMessage) return;
      this.affirmObserver?.disconnect();
      this.affirmMessage = message;
      this.affirmRow = this.querySelector('[data-ow-affirm-row]');
      if (!this.affirmMessage) return;
      this.affirmObserver = new MutationObserver(() => this.syncAffirmVisibility());
      this.affirmObserver.observe(this.affirmMessage, { childList: true, subtree: true, characterData: true });
      this.syncAffirmVisibility();
    }

    syncAffirmVisibility() {
      if (!this.affirmRow || !this.affirmMessage) return;
      this.affirmRow.hidden = this.affirmMessage.textContent.trim().length === 0;
    }

    refreshAffirm() {
      const affirm = window.affirm;
      if (!affirm?.ui?.ready) return;
      affirm.ui.ready(() => affirm.ui.refresh());
    }
  }
);
